import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { useNavigationContext } from "./navigation-context"
import { matchesPath } from "./navigation-utils"
import { NavigationLink } from "./navigation-link"
import { NavigationBadge, type NavigationBadgeType } from "./navigation-badge"
import { useNavigationGroupContext } from "./navigation-group-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationGroupItemProps = {
  label: string
  /**
   * Pathname prefix to match against `Navigation`'s `currentPath`. The item
   * is active when `currentPath.startsWith(pathPrefix)`.
   */
  pathPrefix?: string
  /**
   * Description shown in the tooltip on hover. When a badge is in effect
   * (own `badge` prop or inherited from the enclosing `Navigation.Group`),
   * the tooltip also renders a coloured "Paid feature" / "Beta feature"
   * callout beneath the description.
   */
  tooltip?: string
  render?: useRender.RenderProp
  /**
   * Flags the item as a paid or beta feature displaying an icon. If unset,
   * the item still inherits the enclosing `Navigation.Group`'s badge for
   * its tooltip callout (without rendering an icon on the row itself).
   */
  badge?: NavigationBadgeType
  /**
   * Renders at the right edge of the row. Use for counters or other trailing
   * decorations.
   */
  trailing?: React.ReactNode
}

// =============================================================================
// Sub-components
// =============================================================================

function Dot() {
  return (
    <span
      aria-hidden
      className="isolate flex size-6 shrink-0 items-center justify-center"
    >
      <span className="size-[7px] rounded-full bg-gray-300 transition-colors group-data-active:bg-gray-900" />
    </span>
  )
}

// =============================================================================
// Component
// =============================================================================

/**
 * A child entry rendered inside a `Navigation.Group`.
 * Must be placed directly inside `Navigation.Group`.
 *
 * Renders a label with a dot on the left. The parent group draws the vertical
 * line connecting siblings; the dot belongs to this component and changes
 * colour when the item is active.
 *
 * Pass a `render` prop to inject your own link component (e.g. Inertia `Link`)
 * so the DS doesn't need to know about routing.
 *
 * @example
 * <Navigation.Group icon="bolt" label="Automation">
 *   <Navigation.GroupItem label="Sequences" render={<Link href="/sequences" />} />
 *   <Navigation.GroupItem label="Rules" render={<Link href="/rules" />} />
 * </Navigation.Group>
 */
export function NavigationGroupItem({
  label,
  pathPrefix,
  tooltip,
  render,
  badge,
  trailing
}: NavigationGroupItemProps) {
  const { currentPath } = useNavigationContext()
  const { inheritedBadge } = useNavigationGroupContext()
  const isActive = matchesPath(currentPath, pathPrefix)

  // The badge icon only renders when the item has its OWN badge — inheritance
  // never cascades the icon. The tooltip callout, on the other hand, uses an
  // effective badge so children of a paid group still flag the tooltip.
  const effectiveBadge = badge ?? inheritedBadge

  return (
    <li data-slot="navigation-group-item">
      <NavigationLink
        isActive={isActive}
        tooltip={tooltip}
        badge={effectiveBadge}
        render={render}
      >
        <Dot />
        <span className="flex min-w-0 items-center gap-x-2">
          <span className="flex min-w-0 flex-1 items-center gap-x-2">
            <span className="min-w-0 truncate text-left font-medium">
              {label}
            </span>
            {badge && <NavigationBadge type={badge} />}
          </span>
          {trailing}
        </span>
      </NavigationLink>
    </li>
  )
}

NavigationGroupItem.displayName = "Navigation.GroupItem"
